/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ChangeDetectorRef,
  Component,
  inject,
  input,
  OnInit,
  signal
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import {
  finalize,
  tap,
  switchMap,
  of,
  map,
  distinctUntilChanged,
  filter
} from 'rxjs';
import { SharedModule } from '~app/app/~shared/modules/shared.module';
import { BrandService } from '~app/app/~shared/services/brand.service';
import { ResourceFacade } from '~app/app/~store/facades/resource.facade';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BaseComponent } from '~app/app/~shared/abstract-components/base.component';
import { MatDialogRef } from '@angular/material/dialog';
import { Brand, BrandData } from '~core/models/brand.models';
import { BrandCreationDialogComponent } from '../../modals/brand-creation-dialog/brand-creation-dialog.component';
import { SUPPORTED_IMAGE_EXTENSIONS } from '~app/app/~shared/constants/global.constants';
import { TagService } from '~app/app/~shared/services/tag.service';
import { AdvertiserService } from '~app/app/~shared/services/advertiser.service';
import { Tag, TagType } from '~core/models/tag.models';
import { removeNilProperties } from '~app/app/~shared/utils/utils';
import { Advertiser } from '~core/models/advertiser.models';
import { NgSelectSearchService } from '~app/app/~shared/services/ng-select-search.service';

@Component({
  selector: 'brand-creation-form',
  templateUrl: './brand-creation-form.component.html',
  styleUrl: './brand-creation-form.component.scss',
  standalone: true,
  imports: [SharedModule]
})
export class BrandCreationFormComponent
  extends BaseComponent
  implements OnInit
{
  brand = input<Brand | null>(null);

  private _brandService = inject(BrandService);
  private _resourceFacade = inject(ResourceFacade);
  private _dialogRef = inject(MatDialogRef<BrandCreationDialogComponent>);
  private _tagService = inject(TagService);
  private _advertiserService = inject(AdvertiserService);
  private cdr = inject(ChangeDetectorRef);
  private ngSelectSearchService = inject(NgSelectSearchService);

  advertiserPagination = this.ngSelectSearchService.create<Advertiser>(
    this.cdr
  );

  groupPagination = this.ngSelectSearchService.create<Tag>(this.cdr);

  sectorPagination = this.ngSelectSearchService.create<Tag>(this.cdr);

  subSectorPagination = this.ngSelectSearchService.create<Tag>(this.cdr);

  private readonly pageSize = 10;

  violations$ = this._globalFacade.formViolations$.pipe(
    tap({
      next: violations => {
        setTimeout(() => {
          violations?.forEach(violation =>
            this.form.get(violation.propertyPath)?.setErrors({
              httpError: violation.title
            })
          );
        }, 500);
      }
    })
  );

  isSubmitting = signal(false);
  isHovered = signal(false);
  logo = signal<File | undefined>(undefined);
  logoUrl = signal<string>('');

  form = new FormGroup({
    name: new FormControl<string>('', {
      validators: [Validators.required, Validators.min(3)]
    }),
    groupId: new FormControl<string | null>(null, {
      validators: [Validators.required]
    }),
    sectorId: new FormControl<string | null>(null, {
      validators: [Validators.required]
    }),
    subSectorId: new FormControl<string | null>(null, {
      validators: [Validators.required]
    }),
    organizationId: new FormControl<string | null>(null, {
      validators: [Validators.required]
    }),
    description: new FormControl<string>(''),
    logo: new FormControl(null, {})
  });

  supportedImageExtensions = SUPPORTED_IMAGE_EXTENSIONS;

  ngOnInit(): void {
    const brand = this.brand();
    if (brand) {
      this.logoUrl.set(brand.logoUrl);
      this.form.get('logo')?.clearValidators();
      this.form.patchValue(
        {
          name: brand.name,
          groupId: brand.group.id,
          organizationId: brand.organization?.id,
          description: brand.description
        },
        { emitEvent: false }
      );
      this.loadSectorAndSubSector(brand);
    }

    this.advertiserWireSearch();
    this.groupeWireSearch();
    this.sectorWireSearch();
    this.subSectorWireSearch();
  }

  loadSectorAndSubSector(brand: Brand): void {
    this.sectorPagination
      .initSearch(params =>
        this._tagService.list({
          ...params,
          type: TagType.SECTOR,
          parentId: brand.group.id,
          itemPerPage: this.pageSize
        })
      )
      .pipe(
        tap(() => {
          this.form.patchValue(
            { sectorId: brand.sector.id },
            { emitEvent: false }
          );
          this.subSectorPagination
            .initSearch(params =>
              this._tagService.list({
                ...params,
                type: TagType.SUB_SECTOR,
                parentId: brand.sector.id,
                itemPerPage: this.pageSize
              })
            )
            .pipe(
              tap(() => {
                this.form.patchValue(
                  { subSectorId: brand.subSector.id },
                  { emitEvent: false }
                );
                this.cdr.markForCheck();
              })
            )
            .subscribe();

          this.subSectorPagination.search('');
          this.cdr.markForCheck();
        })
      )
      .subscribe();

    this.sectorPagination.search('');
  }

  // Initialisation Annonceurs lors de la sélection et du scroll

  advertiserWireSearch(): void {
    this.advertiserPagination
      .initSearch(params => this._advertiserService.list(params), {
        itemPerPage: this.pageSize
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();

    this.advertiserPagination.search('');
  }

  onAdvertiserScroll(): void {
    this.advertiserPagination
      .loadMore(params =>
        this._advertiserService.list({
          ...params,
          itemPerPage: this.pageSize
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  // Initialisation Groupe lors de la sélection et du scroll

  groupeWireSearch(): void {
    this.groupPagination
      .initSearch(params => this._tagService.list(params), {
        type: TagType.GROUP,
        itemPerPage: this.pageSize
      })
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();

    this.groupPagination.search('');
  }

  onGroupeScroll(): void {
    this.groupPagination
      .loadMore(params =>
        this._tagService.list({
          ...params,
          type: TagType.GROUP,
          itemPerPage: this.pageSize
        })
      )
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe();
  }

  onClearGroupe(): void {
    const sectorControl = this.form.controls.sectorId;
    const subSectorControl = this.form.controls.subSectorId;
    this.groupPagination.clear();
    sectorControl.reset();
    subSectorControl.reset();
    this.sectorPagination.reset();
    this.subSectorPagination.reset();
  }

  // Initialisation Secteurs lors de la sélection et du scroll

  sectorWireSearch(): void {
    const groupIdControl = this.form.controls.groupId;
    groupIdControl.valueChanges
      .pipe(
        filter(Boolean),
        tap(groupId => {
          this.onClearGroupe();
          const searchParams = {
            type: TagType.SECTOR,
            parentId: groupId,
            itemPerPage: this.pageSize
          };
          this.cdr.markForCheck();
          this.sectorPagination
            .initSearch(params =>
              this._tagService.list({
                ...params,
                ...searchParams
              })
            )
            .subscribe();

          this.sectorPagination.search('');
        }),

        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  onSectorScroll(): void {
    this.form.controls.groupId.valueChanges
      .pipe(
        distinctUntilChanged(),
        switchMap(groupId => {
          return this.sectorPagination.initSearch(params =>
            this._tagService.list({
              ...params,
              type: TagType.SECTOR,
              parentId: groupId,
              itemPerPage: this.pageSize
            })
          );
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  onClearSector(): void {
    const subSectorControl = this.form.controls.subSectorId;
    this.sectorPagination.clear();
    subSectorControl.reset();
    this.subSectorPagination.reset();
  }

  // Initialisation Sous-secteurs lors de la sélection et du scroll

  subSectorWireSearch(): void {
    const sectorIdControl = this.form.controls.sectorId;
    sectorIdControl.valueChanges
      .pipe(
        filter(Boolean),
        tap(sectorId => {
          this.form.get('subSectorId')?.setValue(null, { emitEvent: false });
          this.subSectorPagination.reset();
          const searchParams = {
            type: TagType.SUB_SECTOR,
            parentId: sectorId,
            itemPerPage: this.pageSize
          };
          this.subSectorPagination
            .initSearch(params =>
              this._tagService.list({
                ...params,
                ...searchParams
              })
            )
            .subscribe();
          this.subSectorPagination.search('');
        }),

        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  onSubSectorScroll(): void {
    this.form.controls.sectorId.valueChanges
      .pipe(
        distinctUntilChanged(),
        switchMap(sectorId => {
          return this.sectorPagination.initSearch(params =>
            this._tagService.list({
              ...params,
              type: TagType.SUB_SECTOR,
              parentId: sectorId,
              itemPerPage: this.pageSize
            })
          );
        }),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe();
  }

  onSubmit(): void {
    this.isSubmitting.set(true);
    const brandId = this.brand()?.id;

    const formValue = this.form.value;

    const values = {
      name: formValue.name || '',
      sectorId: formValue.sectorId as string,
      subSectorId: formValue.subSectorId as string,
      groupId: formValue.groupId as string,
      organizationId: formValue.organizationId as string,
      description: formValue.description as string,
      logo: formValue.logo as unknown as File
    };

    if (brandId) {
      this.updateBrand(brandId, values);
    } else {
      this.createBrand(values);
    }
  }

  createBrand(values: BrandData): void {
    const data = removeNilProperties(values);
    this._brandService
      .createWithFormData(data as BrandData)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap({
          next: () => {
            this._dialogRef.close(true);
            this._snackbarService.open({
              text: `Marque créée avec succès`,
              type: 'success'
            });

            this._resourceFacade.getData({ refresh: true });
          }
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  updateBrand(brandId: string, values: BrandData): void {
    const { logo, ...rest } = values;
    let updateObservable = of(rest);
    if (logo) {
      updateObservable = this._brandService
        .updateLogo(logo, brandId)
        .pipe(map(logoUrl => ({ ...rest, logoUrl })));
    }
    updateObservable
      .pipe(
        switchMap(updatedValues =>
          this._brandService.update(brandId, updatedValues)
        ),
        takeUntilDestroyed(this._destroyRef),
        tap(() => {
          this._dialogRef.close(true);
          this._snackbarService.open({
            text: `Marque modifiée avec succès`,
            type: 'success'
          });

          this._resourceFacade.getData({ refresh: true });
        }),
        finalize(() => this.isSubmitting.set(false))
      )
      .subscribe();
  }

  onFileChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];

    if (file) {
      this.setLogo(file);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isHovered.set(true);
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();

    this.isHovered.set(false);
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();

    const fileList = event.dataTransfer?.files;
    this.isHovered.set(false);
    const file = fileList?.[0];

    if (file) {
      this.setLogo(file);
    }
  }

  setLogo(logo: any): void {
    this.form.patchValue({ logo });
    this.form.get('logo')?.updateValueAndValidity();
    this.logo.set(logo);

    this.logoUrl.set(URL.createObjectURL(this.logo() as File));
  }
}
